/** @file Returns the built-in ASCII bitmap font atlas: original 5x9 glyphs drawn at 2x in 12x20 cells. */
import { bytesToBase64 } from './bytesToBase64';
import { encodeGlyphPage } from './encodeGlyphPage';
import type { FontAtlas } from './fontAtlas';
import { GLYPH_ROWS } from './glyphRows.data';

/** Glyph design pixels become SCALE x SCALE page pixels (integer, so nearest and 1:1 sampling stay crisp). */
const SCALE = 2;
const CELL_W = 12;
const CELL_H = 20;
const COLUMNS = 16;
const FIRST_CODE = 32;
const PAGE_W = CELL_W * COLUMNS;
const PAGE_H = CELL_H * Math.ceil(GLYPH_ROWS.length / COLUMNS);

let cached: FontAtlas | null = null;

/** Writes glyph `index` into the mask: 1 px margin left/top, each `#` as a SCALE x SCALE block. */
function drawGlyph(mask: Uint8Array, index: number, rows: string): void {
  const cellX = (index % COLUMNS) * CELL_W, cellY = Math.floor(index / COLUMNS) * CELL_H;
  rows.split('|').forEach((row, gy) => {
    for (let gx = 0; gx < row.length; gx++) {
      if (row.charAt(gx) !== '#') continue;
      for (let k = 0; k < SCALE * SCALE; k++) {
        const x = cellX + 1 + gx * SCALE + (k % SCALE), y = cellY + 1 + gy * SCALE + Math.floor(k / SCALE);
        mask[y * PAGE_W + x] = 1;
      }
    }
  });
}

/** Builds the page mask, encodes it and maps every code to its cell UVs. */
function buildAtlas(): FontAtlas {
  const mask = new Uint8Array(PAGE_W * PAGE_H);
  const glyphs = new Map<number, [number, number, number, number]>();
  GLYPH_ROWS.forEach((rows, index) => {
    drawGlyph(mask, index, rows);
    const x = (index % COLUMNS) * CELL_W, y = Math.floor(index / COLUMNS) * CELL_H;
    glyphs.set(FIRST_CODE + index, [x / PAGE_W, y / PAGE_H, (x + CELL_W) / PAGE_W, (y + CELL_H) / PAGE_H]);
  });
  const pngBase64 = bytesToBase64(encodeGlyphPage(mask, PAGE_W, PAGE_H));
  return { pngBase64, pageW: PAGE_W, pageH: PAGE_H, glyphW: CELL_W, glyphH: CELL_H, glyphs };
}

/** The bundled default font (12 x 20 px cells, ASCII 32-126); built once, then the same object is returned. */
export function defaultFont(): FontAtlas {
  cached ??= buildAtlas();
  return cached;
}
